import { useCallback, useState } from 'react';
import { useBackgroundRefetch } from '@/components/AppBar/NetworkPopover/hooks/useNetworks';
import { toaster } from '@/components/ui/toaster';
import type { BulkNetworkActionFailure, BulkNetworkActionResult } from '@/services/db/actions/network/runForEachNetwork';
import { showError } from '@/utils/toast';
import { TOAST_DURATIONS } from '@/utils/toastDurations';
import { trpc } from '@/utils/trpc';
export type BulkNetworkAction = 'delete' | 'retire' | 'unretire';
export type BulkNetworkTarget = {
  id: string;
  name: string;
};
const PAST_TENSE: Record<BulkNetworkAction, string> = {
  delete: 'Deleted',
  retire: 'Retired',
  unretire: 'Unretired'
};

// Failures are reported in one summary toast, so the global "Action failed"
// fallback would only duplicate it.
const MUTATION_OPTIONS = {
  meta: {
    suppressGlobalToast: true
  }
};
const pluralizeNetworks = (count: number): string => `${count} network${count === 1 ? '' : 's'}`;
const errorMessage = (error: unknown): string => error instanceof Error ? error.message : String(error);
function reportBulkResult(action: BulkNetworkAction, targets: readonly BulkNetworkTarget[], {
  succeededIds,
  failures
}: BulkNetworkActionResult): void {
  if (failures.length === 0) {
    toaster.create({
      type: 'success',
      title: `${PAST_TENSE[action]} ${pluralizeNetworks(succeededIds.length)}`,
      duration: TOAST_DURATIONS.standard
    });
    return;
  }
  const nameById = new Map(targets.map(target => [target.id, target.name]));
  showError(`${PAST_TENSE[action]} ${succeededIds.length} of ${pluralizeNetworks(targets.length)}`, {
    description: failures.map(failure => `${nameById.get(failure.id) ?? failure.id}: ${failure.message}`).join('; ')
  });
}

/** Batch delete / retire / unretire for the Network browser (KBE-964). `run`
 * never throws: every outcome, a failed request included, ends up in one
 * summary toast, and the list refreshes once per batch. It resolves to the ids
 * that succeeded so the caller can react (e.g. leave a network it just deleted). */
export default function useBulkNetworkActions(): {
  run: (action: BulkNetworkAction, targets: readonly BulkNetworkTarget[]) => Promise<string[]>;
  isPending: boolean;
  pendingDeleteIds: ReadonlySet<string>;
} {
  const refetchInBackground = useBackgroundRefetch();
  const {
    mutateAsync: deleteAsync
  } = trpc.network.deleteNetworks.useMutation(MUTATION_OPTIONS);
  const {
    mutateAsync: retireAsync
  } = trpc.network.retireNetworks.useMutation(MUTATION_OPTIONS);
  const {
    mutateAsync: unretireAsync
  } = trpc.network.unretireNetworks.useMutation(MUTATION_OPTIONS);
  const [isPending, setIsPending] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<ReadonlySet<string>>(new Set());

  // One request per network, sequentially. A delete of a 3.X copy also removes
  // its fork-point snapshot, so a whole batch in one request held the
  // connection open for over a minute, and its lost response was re-sent: the
  // re-send found every network already gone. Each request here is as long as
  // a single delete from the row menu.
  const deleteOneByOne = useCallback(async (targets: readonly BulkNetworkTarget[]): Promise<BulkNetworkActionResult> => {
    const succeededIds: string[] = [];
    const failures: BulkNetworkActionFailure[] = [];
    const progressToastId = toaster.create({
      type: 'loading',
      title: `Deleting ${pluralizeNetworks(targets.length)}…`
    });
    try {
      for (const [index, target] of targets.entries()) {
        toaster.update(progressToastId, {
          title: `Deleting ${index + 1} of ${targets.length}…`,
          description: target.name
        });
        try {
          const result = await deleteAsync({
            ids: [target.id]
          });
          succeededIds.push(...result.succeededIds);
          failures.push(...result.failures);
        } catch (error) {
          failures.push({
            id: target.id,
            message: errorMessage(error)
          });
        }
      }
    } finally {
      toaster.dismiss(progressToastId);
    }
    return {
      succeededIds,
      failures
    };
  }, [deleteAsync]);
  const run = useCallback(async (action: BulkNetworkAction, targets_0: readonly BulkNetworkTarget[]): Promise<string[]> => {
    setIsPending(true);
    const ids = targets_0.map(target => target.id);
    let succeededIds_0: string[] = [];
    // Merged, never replaced: a deleted row stays hidden until the refetch
    // drops it, even if another batch starts meanwhile. Deleted ids are never
    // un-hidden, since a deleted network cannot come back.
    if (action === 'delete') {
      setPendingDeleteIds(previous => new Set([...previous, ...ids]));
    }
    try {
      let result: BulkNetworkActionResult;
      if (action === 'delete') {
        result = await deleteOneByOne(targets_0);
      } else {
        const mutate = action === 'retire' ? retireAsync : unretireAsync;
        result = await mutate({
          ids
        }).catch((error: unknown) => ({
          succeededIds: [],
          failures: ids.map(id => ({
            id,
            message: errorMessage(error)
          }))
        }));
      }
      succeededIds_0 = result.succeededIds;
      reportBulkResult(action, targets_0, result);
      return succeededIds_0;
    } finally {
      if (action === 'delete') {
        const deleted = new Set(succeededIds_0);
        setPendingDeleteIds(previous => {
          const next = new Set(previous);
          for (const id of ids) {
            if (!deleted.has(id)) next.delete(id);
          }
          return next;
        });
      }
      setIsPending(false);
      refetchInBackground();
    }
  }, [deleteOneByOne, refetchInBackground, retireAsync, unretireAsync]);
  return {
    run,
    isPending,
    pendingDeleteIds
  };
}